import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import '@aytm/lib/rfd-ui/styles/advanced/tabbed-sidebar.scss'

export { TabbedSidebar }
export default React.memo(
  TabbedSidebar,
  (prevProps, nextProps) =>
    prevProps.tabID === nextProps.tabID &&
    prevProps.defaultID === nextProps.defaultID &&
    prevProps.className === nextProps.className &&
    prevProps.tabs === nextProps.tabs &&
    prevProps.onTabChange === nextProps.onTabChange
)

const TabbedSidebarTabsMemo = React.memo(
  TabbedSidebarTabs,
  (prevProps, nextProps) =>
    prevProps.tabIndex === nextProps.tabIndex &&
    prevProps.tabs === nextProps.tabs &&
    prevProps.className === nextProps.className &&
    prevProps.onTabChange === nextProps.onTabChange
)

TabbedSidebar.propTypes = {
  /**
   * Panel custom className
   */
  className: PropTypes.string,
  /**
   * Tab custom className
   */
  tabClassName: PropTypes.string,
  /**
   * Current tab id
   */
  tabID: PropTypes.string,
  /**
   * Default tab id
   */
  defaultID: PropTypes.string,
  /**
   * onTabChange callback
   * @param {string} tabID
   */
  onTabChange: PropTypes.func,
  /**
   * Sidebar tabs
   * @typedef {Object} Tabs
   * @property {string} id - tab id
   * @property {(function|ReactNode)} tab - what to render inside tab
   * @property {(function|ReactNode)} panel - what to render inside sidebar (function works as lazy render)
   */
  tabs: PropTypes.array.isRequired
}
function TabbedSidebar({ className, tabClassName, tabID, defaultID, tabs = [], onTabChange } = {}) {
  const [currentTabID, setCurrentTabID] = useState(tabID)
  const tabsIsArray = Array.isArray(tabs)
  let currentTabIndex = tabsIsArray ? tabs.findIndex(tab => tab.id === currentTabID) : -1
  if (currentTabID && -1 === currentTabIndex && tabsIsArray) {
    currentTabIndex = tabs.findIndex(tab => tab.id === defaultID)
    if (-1 === currentTabIndex) {
      currentTabIndex = 0
    }
  }
  const currentPanel =
    tabsIsArray && 0 <= currentTabIndex
      ? tabs[currentTabIndex] && tabs[currentTabIndex].panel
        ? 'function' === typeof tabs[currentTabIndex].panel
          ? tabs[currentTabIndex].panel()
          : tabs[currentTabIndex].panel
        : null
      : null
  const lastPropTabID = useRef(tabID)
  useEffect(() => {
    if (lastPropTabID.current !== tabID) {
      lastPropTabID.current = tabID
      setCurrentTabID(tabID)
    }
  }, [tabID])

  return (
    <div className="rfd-ui-tabbed-sidebar">
      <div className="rfd-ui-tabbed-sidebar-tabs">
        <div className="rfd-ui-tabbed-sidebar-tabs-container" role="tablist">
          <TabbedSidebarTabsMemo
            tabs={tabs}
            className={tabClassName}
            tabIndex={currentTabIndex}
            setTabID={setCurrentTabID}
            onTabChange={onTabChange}
          />
        </div>
      </div>
      <div className={`rfd-ui-tabbed-sidebar-panel${className ? ' ' + className : ''}`} role="tabpanel">
        <div className="rfd-ui-tabbed-sidebar-panel-container">{currentPanel}</div>
      </div>
    </div>
  )
}

TabbedSidebarTabs.propTypes = {
  tabs: PropTypes.array.isRequired,
  setTabID: PropTypes.func.isRequired,
  tabIndex: PropTypes.number,
  onTabChange: PropTypes.func,
  className: PropTypes.string
}
function TabbedSidebarTabs({ className, setTabID, tabIndex, tabs = [], onTabChange } = {}) {
  // map tabs
  const tabElements = tabs.map((tab, thisTabIndex) => (
    <div
      className={`rfd-ui-tabbed-sidebar-tab${tabIndex === thisTabIndex ? ' rfd-ui-tabbed-sidebar-tab-active' : ''}${
        className ? ' ' + className : ''
      }${tabIndex != null && thisTabIndex === tabIndex - 1 ? ' rfd-ui-tabbed-sidebar-tab-prev' : ''}${
        tabIndex != null && tabIndex >= 0 && thisTabIndex === tabIndex + 1 ? ' rfd-ui-tabbed-sidebar-tab-next' : ''
      }`}
      onClick={() => {
        setTabID(tab.id)
        onTabChange && 'function' === typeof onTabChange && onTabChange(tab.id)
      }}
      role="tab"
      aria-selected={tabIndex === thisTabIndex}
      key={thisTabIndex}
    >
      {'function' === typeof tab.tab ? tab.tab() : tab.tab}
    </div>
  ))
  // add terminator
  tabElements.push(
    <div
      className={`rfd-ui-tabbed-sidebar-tab rfd-ui-tabbed-sidebar-tab-terminator${
        tabIndex === tabs.length - 1 ? ' rfd-ui-tabbed-sidebar-tab-next' : ''
      }`}
      role="none"
      key={null}
    >
      &nbsp;
    </div>
  )
  return tabElements
}
